import React from 'react';
import { Alert, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, Link, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';

export interface EventOverviewEvent {
  category?: string;
  listingCityName?: string;
  listingStateName?: string;
  participatingUsers?: string[];
  maxParticipants?: string | number;
}

export interface MathTradeSummary {
  totalItems: number;
  totalMyItems: number;
  totalWithPreferences: number;
  inputState?: string;
  publishedIsFinal?: boolean;
  publishedVisible?: boolean;
  previewVisible?: boolean;
  tradeCutoffPassed?: boolean;
}

interface EventInfoGridProps {
  event: EventOverviewEvent;
  eventDateLabel: string | null;
  tradeCutoffLabel: string | null;
  mustArriveByLabel: string | null;
}

export function EventInfoGrid({ event, eventDateLabel, tradeCutoffLabel, mustArriveByLabel }: EventInfoGridProps) {
  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {event.category && (
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon sx={{ color: '#667eea' }} />
              <Box>
                <Typography variant="caption" color="textSecondary">Category</Typography>
                <Typography variant="body2" sx={{ fontWeight: '600' }}>{event.category}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      )}

      {(event.listingCityName || event.listingStateName) && (
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon sx={{ color: '#667eea' }} />
              <Box>
                <Typography variant="caption" color="textSecondary">Location</Typography>
                <Typography variant="body2" sx={{ fontWeight: '600' }}>{event.listingCityName}, {event.listingStateName}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      )}

      {eventDateLabel && <EventInfoValue label="Event Date & Time" value={eventDateLabel} />}
      {tradeCutoffLabel && <EventInfoValue label="Trade Cutoff" value={tradeCutoffLabel} />}
      {mustArriveByLabel && <EventInfoValue label="Must Arrive By" value={mustArriveByLabel} />}

      <Grid item xs={12} sm={6} md={3}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon sx={{ color: '#667eea' }} />
            <Box>
              <Typography variant="caption" color="textSecondary">Participants</Typography>
              <Typography variant="body2" sx={{ fontWeight: '600' }}>
                {(event.participatingUsers || []).length}{event.maxParticipants ? ` / ${event.maxParticipants}` : ''}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

function EventInfoValue({ label, value }: { label: string; value: string }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Paper elevation={1} sx={{ p: 2, borderRadius: '12px' }}>
        <Typography variant="caption" color="textSecondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>{value}</Typography>
      </Paper>
    </Grid>
  );
}

export function MathTradeSnapshot({ summary }: { summary: MathTradeSummary }) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', mb: 3, backgroundColor: '#f8f9fa' }}>
      <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Math Trade Snapshot</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Alert severity="info" sx={{ flex: 1 }}>Event items in trade pool: <strong>{summary.totalItems}</strong></Alert>
        <Alert severity="success" sx={{ flex: 1 }}>Your items in this event: <strong>{summary.totalMyItems}</strong></Alert>
        <Alert severity={summary.totalMyItems === 0 || summary.totalWithPreferences < summary.totalMyItems ? 'warning' : 'success'} sx={{ flex: 1 }}>
          Your items with wants: <strong>{summary.totalWithPreferences}</strong>
        </Alert>
        <Alert severity={summary.tradeCutoffPassed ? 'warning' : 'info'} sx={{ flex: 1 }}>
          Status: <strong>{summary.tradeCutoffPassed ? 'Locked' : (summary.inputState || 'Collecting')}</strong>
        </Alert>
      </Box>
      {(summary.previewVisible || summary.publishedVisible) && (
        <Box sx={{ mt: 2 }}>
          {summary.previewVisible && !summary.publishedIsFinal && <Alert severity="warning">A private preview exists. It is not final and may change before sharing or cutoff.</Alert>}
          {summary.publishedVisible && !summary.publishedIsFinal && <Alert severity="info" sx={{ mt: 1 }}>A shared preview is visible to participants. It is not final until the cutoff is reached.</Alert>}
          {summary.publishedVisible && summary.publishedIsFinal && <Alert severity="success" sx={{ mt: 1 }}>Final published trade results are available for this event.</Alert>}
        </Box>
      )}
    </Paper>
  );
}

interface Participant {
  userId: string;
  displayName: string;
  userName?: string;
  avatarUrl?: string | null;
  profileTag?: string | null;
  joinedAt?: string | null;
  completedTradeCount?: number;
  reviewCount?: number;
  trustScore?: number | null;
}

interface EventParticipantsPanelProps {
  ownerId?: string;
  participants: Participant[];
  expanded: boolean;
  loading: boolean;
  error: string | null;
  onToggle: () => void;
  onOpenProfile: (userId: string) => void;
  formatJoinedDate: (value?: string | null) => string;
}

export function EventParticipantsPanel({ ownerId, participants, expanded, loading, error, onToggle, onOpenProfile, formatJoinedDate }: EventParticipantsPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', mb: 3, backgroundColor: '#f8f9fa' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: '600', color: '#333' }}>Participants</Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>See who is in the event, when they joined CrowdTrades, how many completed trades they have, and jump to their trader profile.</Typography>
        </Box>
        <Button variant="outlined" onClick={onToggle}>{expanded ? 'Hide Participants' : `Show Participants${participants.length ? ` (${participants.length})` : ''}`}</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!expanded && !loading && <Alert severity="info" sx={{ mt: 2 }}>Hidden by default to keep the page easier to scan.</Alert>}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box> : participants.length > 0 ? (
            <Stack spacing={1.5}>{participants.map((participant, index) => (
              <Paper key={participant.userId} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Avatar src={participant.avatarUrl || undefined} sx={{ width: 32, height: 32 }}>{participant.displayName?.charAt(0) || '?'}</Avatar>
                      <Link component="button" type="button" underline="hover" onClick={() => onOpenProfile(participant.userId)} sx={{ fontWeight: 600, fontSize: '1rem', textAlign: 'left' }}>{participant.displayName}</Link>
                      {participant.userName && <Typography variant="body2" color="text.secondary">@{participant.userName}</Typography>}
                      {participant.profileTag && <Chip size="small" label={participant.profileTag} color="secondary" />}
                      {participant.userId === ownerId && <Chip size="small" label="Host" color="secondary" />}
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 2 }} sx={{ mt: 0.75 }}>
                      <Typography variant="body2" color="text.secondary">Joined: {formatJoinedDate(participant.joinedAt)}</Typography>
                      <Typography variant="body2" color="text.secondary">Completed trades: {participant.completedTradeCount || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Reviews: {participant.reviewCount || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Rating: {participant.trustScore != null ? participant.trustScore : 'Not implemented yet'}</Typography>
                    </Stack>
                  </Box>
                </Stack>
                {index < participants.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Paper>
            ))}</Stack>
          ) : <Alert severity="info">No participants found yet.</Alert>}
        </Box>
      </Collapse>
    </Paper>
  );
}

interface EventRemindersPanelProps {
  availableOffsets: number[];
  selectedOffsets: number[];
  saving: boolean;
  error: string | null;
  onToggleOffset: (offset: number) => void;
  onSave: () => void;
}

export function EventRemindersPanel({ availableOffsets, selectedOffsets, saving, error, onToggleOffset, onSave }: EventRemindersPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', mb: 3, backgroundColor: '#f8f9fa' }}>
      <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Event Reminders</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>
        Pick when you want mailbox reminders before this event. Only relevant options are shown.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {availableOffsets.length > 0 ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <FormGroup>
            {availableOffsets.map((days) => (
              <FormControlLabel
                key={days}
                control={<Checkbox checked={selectedOffsets.includes(days)} onChange={() => onToggleOffset(days)} />}
                label={`${days} day${days === 1 ? '' : 's'} before`}
              />
            ))}
          </FormGroup>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              Selected: {selectedOffsets.length > 0 ? selectedOffsets.map((days) => `${days}d`).join(', ') : 'none'}
            </Typography>
            <Button variant="contained" onClick={onSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: '600', alignSelf: 'flex-start' }}>
              {saving ? 'Saving...' : 'Save Reminders'}
            </Button>
          </Box>
        </Box>
      ) : <Alert severity="info">No advance reminder windows are still relevant for this event.</Alert>}
    </Paper>
  );
}

interface EventMessagingPanelProps {
  expanded: boolean;
  subject: string;
  body: string;
  saving: boolean;
  error: string | null;
  success: string | null;
  onToggle: () => void;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onSend: () => void;
}

export function EventMessagingPanel({ expanded, subject, body, saving, error, success, onToggle, onSubjectChange, onBodyChange, onSend }: EventMessagingPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f8f9fa', height: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: '600', color: '#333' }}>Message Everyone</Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>Send a mailbox message to everyone who joined this event. A copy is also saved to your inbox.</Typography>
        </Box>
        <Button variant="outlined" onClick={onToggle}>{expanded ? 'Hide Message Form' : 'Show Message Form'}</Button>
      </Stack>
      {!expanded && <Alert severity="info" sx={{ mt: 2 }}>Hidden by default so the page stays focused on live operations.</Alert>}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Stack spacing={2}>
            <TextField label="Subject" value={subject} onChange={(event) => onSubjectChange(event.target.value)} fullWidth />
            <TextField label="Message" value={body} onChange={(event) => onBodyChange(event.target.value)} fullWidth multiline minRows={4} />
            <Box>
              <Button variant="contained" onClick={onSend} disabled={saving || !subject.trim() || !body.trim()} sx={{ textTransform: 'none', fontWeight: '600' }}>
                {saving ? 'Sending...' : 'Send Message to Participants'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}

interface TimelineEntry {
  id: string;
  type: string;
  message: string;
  createdAt?: string | null;
}

interface EventTimelinePanelProps {
  entries: TimelineEntry[];
  filter: string;
  expanded: boolean;
  loading: boolean;
  error: string | null;
  onToggle: () => void;
  onFilterChange: (filter: string) => void;
  typeLabel: (type: string) => string;
  formatDate: (value?: string | null) => string | null;
  renderSummary: (entry: any) => React.ReactNode;
  metadataChips: (entry: any) => string[];
}

const timelineFilters = [
  ['all', 'All'],
  ['event', 'Event'],
  ['participants', 'Participants'],
  ['items', 'Items'],
  ['messaging', 'Messaging'],
  ['math-trade', 'Math Trade'],
] as const;

export function EventTimelinePanel({ entries, filter, expanded, loading, error, onToggle, onFilterChange, typeLabel, formatDate, renderSummary, metadataChips }: EventTimelinePanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f8f9fa', height: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: '600', color: '#333' }}>Event History</Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>Activity history for event changes, participants, items, and math trade progress.</Typography>
        </Box>
        <Button variant="outlined" onClick={onToggle}>{expanded ? 'Hide Timeline' : `Show Timeline${entries.length ? ` (${entries.length})` : ''}`}</Button>
      </Stack>
      {!expanded && <Alert severity="info" sx={{ mt: 2 }}>Hidden by default so this page stays readable.</Alert>}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
            {timelineFilters.map(([value, label]) => (
              <Chip key={value} label={label} color={filter === value ? 'primary' : 'default'} variant={filter === value ? 'filled' : 'outlined'} onClick={() => onFilterChange(value)} />
            ))}
          </Stack>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box> : entries.length > 0 ? (
            <Stack spacing={1.5}>{entries.map((entry) => {
              const chips = metadataChips(entry);
              return (
                <Paper key={entry.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Chip size="small" color="secondary" variant="outlined" label={typeLabel(entry.type)} />
                      <Typography variant="body2" color="text.secondary">{formatDate(entry.createdAt)}</Typography>
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{renderSummary(entry)}</Typography>
                    <Typography variant="body2" color="text.secondary">{entry.message}</Typography>
                    {chips.length > 0 && <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {chips.slice(0, 5).map((chip) => <Chip key={chip} size="small" variant="outlined" label={chip} />)}
                    </Stack>}
                  </Stack>
                </Paper>
              );
            })}</Stack>
          ) : <Alert severity="info">No timeline entries match that filter yet.</Alert>}
        </Box>
      </Collapse>
    </Paper>
  );
}

interface EventCheckInQrPanelProps {
  eventName?: string;
  targetUrl: string;
  dataUrl: string | null;
  expanded: boolean;
  onToggle: () => void;
  onPrint: () => void;
  onDownload: () => void;
}

export function EventCheckInQrPanel({ eventName, targetUrl, dataUrl, expanded, onToggle, onPrint, onDownload }: EventCheckInQrPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f8f9fa', height: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: '600', color: '#333' }}>Check-in QR Code</Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>Share or print this check-in QR code. It embeds the event ID and is labeled for the event.</Typography>
        </Box>
        <Button variant="outlined" onClick={onToggle}>{expanded ? 'Hide QR Code' : 'Show QR Code'}</Button>
      </Stack>
      {!expanded && <Alert severity="info" sx={{ mt: 2 }}>Hidden by default since it is mostly useful during live check-in.</Alert>}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mt: 2 }}>
          <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'white', border: '1px solid #e5e7eb' }}>
            {dataUrl ? <Box component="img" src={dataUrl} alt={`QR code for ${eventName || 'event'}`} sx={{ width: 220, height: 220, display: 'block' }} /> : (
              <Box sx={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={28} /></Box>
            )}
          </Box>
          <Stack spacing={1.5} sx={{ maxWidth: 420 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{eventName ? `Event: ${eventName}` : 'Event'}</Typography>
            <Typography variant="body2" color="text.secondary">Check-in URL: <strong>{targetUrl}</strong></Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Link component="button" type="button" onClick={onPrint} underline="hover" sx={{ fontWeight: 600, textAlign: 'left' }}>Printable Version</Link>
              <Link component="button" type="button" onClick={onDownload} underline="hover" sx={{ fontWeight: 600, textAlign: 'left' }}>Download Image</Link>
            </Stack>
          </Stack>
        </Stack>
      </Collapse>
    </Paper>
  );
}

interface EventBehaviorPanelProps {
  expanded: boolean;
  sessionId: string;
  subjectUserId: string;
  outcomeType: string;
  notes: string;
  saving: boolean;
  sessions: Array<{ id: string; label: string }>;
  participants: Array<{ id: string; label: string }>;
  records: Array<{ id: string; subjectLabel: string; outcomeType: string; dateLabel: string | null; sessionLabel?: string; notes?: string }>;
  onToggle: () => void;
  onSessionChange: (value: string) => void;
  onSubjectChange: (value: string) => void;
  onOutcomeChange: (value: string) => void;
  onNotesChange: (value: string) => void;
  onRecord: () => void;
}

const behaviorOutcomes = ['successful-handoff-confirmed', 'no-show', 'late-arrival', 'wrong-item-delivered', 'item-condition-dispute', 'admin-resolved-issue'];

export function EventBehaviorPanel({ expanded, sessionId, subjectUserId, outcomeType, notes, saving, sessions, participants, records, onToggle, onSessionChange, onSubjectChange, onOutcomeChange, onNotesChange, onRecord }: EventBehaviorPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f5fbff', height: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: '600', color: '#333' }}>Issue & Handoff Log</Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>Record key outcomes like no-shows, late arrivals, wrong items, or successful handoffs.</Typography>
        </Box>
        <Button variant="outlined" onClick={onToggle}>{expanded ? 'Hide Log Tools' : 'Show Log Tools'}</Button>
      </Stack>
      {!expanded && <Alert severity="info" sx={{ mt: 2 }}>Hidden by default since this is mostly used when something needs to be recorded.</Alert>}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField select label="Related session (optional)" value={sessionId} onChange={(event) => onSessionChange(event.target.value)} fullWidth>
            <MenuItem value="">No specific session</MenuItem>
            {sessions.map((session) => <MenuItem key={session.id} value={session.id}>{session.label}</MenuItem>)}
          </TextField>
          <TextField select label="Participant" value={subjectUserId} onChange={(event) => onSubjectChange(event.target.value)} fullWidth>
            {participants.map((participant) => <MenuItem key={participant.id} value={participant.id}>{participant.label}</MenuItem>)}
          </TextField>
          <TextField select label="Outcome type" value={outcomeType} onChange={(event) => onOutcomeChange(event.target.value)} fullWidth>
            {behaviorOutcomes.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <TextField label="Notes" value={notes} onChange={(event) => onNotesChange(event.target.value)} multiline minRows={2} fullWidth />
          <Box><Button variant="contained" onClick={onRecord} disabled={saving || !subjectUserId}>{saving ? 'Recording...' : 'Record Behavior Outcome'}</Button></Box>
          {records.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Recent event behavior records</Typography>
              <Stack spacing={1}>{records.slice(0, 5).map((record) => (
                <Paper key={record.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{record.subjectLabel} — {record.outcomeType}</Typography>
                  <Typography variant="caption" color="text.secondary">{record.dateLabel}</Typography>
                  {record.sessionLabel && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Session: {record.sessionLabel}</Typography>}
                  {record.notes && <Typography variant="body2" color="text.secondary">{record.notes}</Typography>}
                </Paper>
              ))}</Stack>
            </Paper>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}

interface EventCompletionPanelProps {
  expanded: boolean;
  unfinishedGroupCount: number;
  completing: boolean;
  error: string | null;
  onToggle: () => void;
  onOpenConfirmation: () => void;
}

export function EventCompletionPanel({ expanded, unfinishedGroupCount, completing, error, onToggle, onOpenConfirmation }: EventCompletionPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#fff8f1' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: '600', color: '#333' }}>Wrap Up Event</Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>Close out the event when you are done. If some trade groups did not finish, record why before the event is marked completed.</Typography>
        </Box>
        <Button variant="outlined" color="warning" onClick={onToggle}>{expanded ? 'Hide Wrap-up Tools' : 'Show Wrap-up Tools'}</Button>
      </Stack>
      {!expanded && <Alert severity="info" sx={{ mt: 2 }}>Hidden by default since this is usually the last step of the day.</Alert>}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>Need a plain-English walkthrough? See the <Link href="#/event-day-guide" underline="hover">event-day guide</Link>.</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>Unfinished trade groups right now: <strong>{unfinishedGroupCount}</strong></Alert>
          <Button variant="contained" color="warning" onClick={onOpenConfirmation} disabled={completing}>{completing ? 'Completing Event...' : 'Mark Event Completed'}</Button>
        </Box>
      </Collapse>
    </Paper>
  );
}

interface EventAdminSummaryPanelProps {
  stats: Array<{ label: string; value: number; severity: 'success' | 'info' | 'warning' | 'error' }>;
}

export function EventAdminSummaryPanel({ stats }: EventAdminSummaryPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#eef6ff' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Event Admin Dashboard</Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>Quick snapshot of readiness, session flow, room usage, and anything that needs attention.</Typography>
        </Box>
        <Grid container spacing={1.5}>
          {stats.map((stat) => (
            <Grid item xs={12} sm={6} md={3} key={stat.label}>
              <Alert severity={stat.severity} sx={{ height: '100%' }}>{stat.label}: <strong>{stat.value}</strong></Alert>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Paper>
  );
}

interface EventWatchListPanelProps {
  alerts: Array<{ severity: 'warning' | 'info' | 'error'; message: string; actionLabel?: string; onClick?: () => void }>;
}

export function EventWatchListPanel({ alerts }: EventWatchListPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#fffdf7' }}>
      <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Watch List</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>Attention-needed items pulled from live event state so you can spot blockers fast.</Typography>
      <Stack spacing={1}>
        {alerts.length > 0 ? alerts.map((alert, index) => (
          <Alert
            key={`${alert.message}-${index}`}
            severity={alert.severity}
            action={alert.actionLabel && alert.onClick ? <Button color="inherit" size="small" onClick={alert.onClick}>{alert.actionLabel}</Button> : undefined}
          >
            {alert.message}
          </Alert>
        )) : <Alert severity="success">No immediate issues right now.</Alert>}
      </Stack>
    </Paper>
  );
}

interface TradeGroupCardItem {
  id: string;
  groupKey: string;
  participantCount: number;
  arrivedCount: number;
  busyCount: number;
  readinessState: string;
  status?: string;
}

interface EventTradeGroupsPanelProps {
  groups: TradeGroupCardItem[];
  loading: boolean;
  error: string | null;
  onOpenDetails: (group: TradeGroupCardItem) => void;
  describeBlocker: (group: TradeGroupCardItem) => string;
}

const getTradeGroupStatus = (group: TradeGroupCardItem) => {
  if (group.status === 'completed') return { label: 'Completed', color: 'info' as const };
  if (group.status === 'in-session') return { label: 'In session', color: 'warning' as const };
  if (group.readinessState === 'ready-to-start-trade') return { label: 'Ready to start trade', color: 'success' as const };
  if (group.readinessState === 'ready-when-other-members-finish-other-trades') return { label: 'Ready when other members finish other trades', color: 'warning' as const };
  return { label: 'Not all members have arrived', color: 'default' as const };
};

export function EventTradeGroupsPanel({ groups, loading, error, onOpenDetails, describeBlocker }: EventTradeGroupsPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f8f9fa', height: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Ready Trade Groups</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>See which finalized groups are ready now, still waiting on arrivals, or blocked by other trades.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box> : groups.length > 0 ? (
        <Stack spacing={1.5}>{groups.map((group) => {
          const status = getTradeGroupStatus(group);
          return (
            <Paper key={group.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Group {group.groupKey}</Typography>
                  <Typography variant="body2" color="text.secondary">Participants: {group.participantCount} • Arrived: {group.arrivedCount} • Busy: {group.busyCount}</Typography>
                  <Typography variant="body2" color="text.secondary">{describeBlocker(group)}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button variant="text" onClick={() => onOpenDetails(group)}>Details</Button>
                  <Chip label={status.label} color={status.color} />
                </Stack>
              </Stack>
            </Paper>
          );
        })}</Stack>
      ) : <Alert severity="info">No finalized trade groups found yet.</Alert>}
    </Paper>
  );
}

interface EventStartSessionPanelProps {
  error: string | null;
  saving: boolean;
  selectedGroupId: string;
  selectedLocationId: string;
  readyGroups: Array<{ id: string; groupKey: string; participantCount: number }>;
  locations: Array<{ id: string; name: string; capacity: number; currentSessionId?: string | null }>;
  selectedGroupParticipantCount?: number;
  onGroupChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  onStart: () => void;
}

export function EventStartSessionPanel({ error, saving, selectedGroupId, selectedLocationId, readyGroups, locations, selectedGroupParticipantCount, onGroupChange, onLocationChange, onStart }: EventStartSessionPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f8f9fa', height: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Start a Session</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>Pick a ready group, choose an available location, and stage the session so participants know where to gather.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel>Ready Group</InputLabel>
          <Select value={selectedGroupId} label="Ready Group" onChange={(event) => onGroupChange(event.target.value)}>
            {readyGroups.map((group) => <MenuItem key={group.id} value={group.id}>{`Group ${group.groupKey} (${group.participantCount} participants)`}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Available Location</InputLabel>
          <Select value={selectedLocationId} label="Available Location" onChange={(event) => onLocationChange(event.target.value)}>
            {locations.map((location, index) => <MenuItem key={location.id} value={location.id} disabled={Boolean(location.currentSessionId)}>{`${location.name} (capacity ${location.capacity})${location.currentSessionId ? ' — occupied' : index === 0 && selectedGroupParticipantCount !== undefined ? ' — best fit' : ''}`}</MenuItem>)}
          </Select>
        </FormControl>
        {selectedGroupParticipantCount !== undefined && <Typography variant="body2" color="text.secondary">Suggested locations are sorted by availability and best capacity fit for this {selectedGroupParticipantCount}-participant group.</Typography>}
        <Box><Button variant="contained" onClick={onStart} disabled={saving || !selectedGroupId || !selectedLocationId}>Start Session</Button></Box>
      </Stack>
    </Paper>
  );
}

interface EventSessionsPanelProps {
  sessions: Array<{
    id: string;
    locationName?: string;
    status: string;
    participantCount: number;
    initiatedLabel: string | null;
    initiatedElapsed: string;
    startedElapsed: string;
    cancelReason?: string;
    behaviorRecords: Array<{ id: string; text: string }>;
  }>;
  saving: boolean;
  onOpenDetails: (sessionId: string) => void;
  onCancel: (sessionId: string) => void;
}

const sessionStatusColor = (status: string) => status === 'active' || status === 'completed' ? 'success' : status === 'canceled' ? 'default' : 'warning';

export function EventSessionsPanel({ sessions, saving, onOpenDetails, onCancel }: EventSessionsPanelProps) {
  const count = (status: string) => sessions.filter((session) => session.status === status).length;
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f8f9fa', height: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Sessions</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>Oldest sessions appear first so it is easier to spot anything that may be stuck. This includes staged, active, completed, and canceled sessions.</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        <Alert severity="info" sx={{ flex: 1 }}>Staged: <strong>{count('staged')}</strong></Alert>
        <Alert severity="success" sx={{ flex: 1 }}>Active: <strong>{count('active')}</strong></Alert>
        <Alert severity="success" sx={{ flex: 1 }}>Completed: <strong>{count('completed')}</strong></Alert>
        <Alert severity="warning" sx={{ flex: 1 }}>Canceled: <strong>{count('canceled')}</strong></Alert>
      </Stack>
      {sessions.length > 0 ? <Stack spacing={1.5}>{sessions.map((session) => (
        <Paper key={session.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{session.locationName || 'Assigned location'}</Typography>
              <Typography variant="body2" color="text.secondary">Participants: {session.participantCount} • Initiated: {session.initiatedLabel}</Typography>
              <Typography variant="body2" color="text.secondary">Elapsed since initiated: {session.initiatedElapsed} • Elapsed since started: {session.startedElapsed}</Typography>
              {session.cancelReason && <Typography variant="body2" color="text.secondary">Cancel reason: {session.cancelReason}</Typography>}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="text" onClick={() => onOpenDetails(session.id)}>Details</Button>
              <Chip size="small" label={session.status} color={sessionStatusColor(session.status)} />
              {(session.status === 'staged' || session.status === 'active') && <Button variant="outlined" color="warning" onClick={() => onCancel(session.id)} disabled={saving}>Cancel</Button>}
            </Stack>
          </Stack>
          {session.behaviorRecords.length > 0 && <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#fafcff' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Behavior records for this session</Typography>
            <Stack spacing={0.75}>{session.behaviorRecords.map((record) => <Typography key={record.id} variant="body2" color="text.secondary">{record.text}</Typography>)}</Stack>
          </Paper>}
        </Paper>
      ))}</Stack> : <Alert severity="info">No active trade sessions right now.</Alert>}
    </Paper>
  );
}

interface EventLocationsOverviewPanelProps {
  locations: Array<{ id: string; name: string; capacity: number; active: boolean; occupied: boolean; occupiedAtLabel?: string | null; sessionId?: string; sessionStatus?: string; sessionStartedLabel?: string | null; participants?: string; tradeGroupId?: string }>;
  onOpenSession: (sessionId: string) => void;
  onOpenLocation: (locationId: string) => void;
}

export function EventLocationsOverviewPanel({ locations, onOpenSession, onOpenLocation }: EventLocationsOverviewPanelProps) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f8fbff' }}>
      <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Locations at a Glance</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>Quick view of which locations are available, occupied, or inactive, plus what is happening at each one.</Typography>
      <Stack spacing={1.5}>{locations.map((location) => (
        <Paper key={location.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{location.name}</Typography>
              <Typography variant="body2" color="text.secondary">Capacity: {location.capacity}</Typography>
              {location.occupiedAtLabel && <Typography variant="body2" color="text.secondary">Occupied since: {location.occupiedAtLabel}</Typography>}
              {location.sessionId && <>
                <Typography variant="body2" color="text.secondary">Current session: {location.sessionStatus} • started {location.sessionStartedLabel || 'Unknown'}</Typography>
                <Typography variant="body2" color="text.secondary">Participants: {location.participants}</Typography>
                <Typography variant="body2" color="text.secondary">Trade group: {location.tradeGroupId}</Typography>
              </>}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color={location.active ? 'success' : 'default'} label={location.active ? 'Active' : 'Inactive'} />
              <Chip size="small" color={location.occupied ? 'warning' : 'success'} label={location.occupied ? 'Occupied' : 'Available'} />
              {location.sessionId && <Button variant="text" onClick={() => onOpenSession(location.sessionId!)}>Open Session</Button>}
              <Button variant="text" onClick={() => onOpenLocation(location.id)}>Location Details</Button>
            </Stack>
          </Stack>
        </Paper>
      ))}</Stack>
    </Paper>
  );
}

interface EventCancelSessionDialogProps {
  open: boolean;
  reasonCode: string;
  reasonText: string;
  saving: boolean;
  onClose: () => void;
  onReasonCodeChange: (value: string) => void;
  onReasonTextChange: (value: string) => void;
  onConfirm: () => void;
}

export function EventCancelSessionDialog({ open, reasonCode, reasonText, saving, onClose, onReasonCodeChange, onReasonTextChange, onConfirm }: EventCancelSessionDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cancel Trade Session</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField select label="Reason code" value={reasonCode} onChange={(event) => onReasonCodeChange(event.target.value)} fullWidth>
            {['no-show', 'stuck', 'wrong-item', 'other'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <TextField label="Details" value={reasonText} onChange={(event) => onReasonTextChange(event.target.value)} multiline minRows={3} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button color="warning" variant="contained" onClick={onConfirm} disabled={saving}>Confirm Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

export function EventDetailsDialog({ open, title, maxWidth = 'sm', onClose, children }: { open: boolean; title: string; maxWidth?: 'sm' | 'md'; onClose: () => void; children: React.ReactNode }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={maxWidth}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>{children}</DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

export function TradeGroupPathsPanel({ paths }: { paths: string[] }) {
  if (paths.length === 0) return null;
  return <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Trade paths</Typography>
    <Stack spacing={0.75}>{paths.map((path, index) => <Typography key={`${path}-${index}`} variant="body2" color="text.secondary">{path}</Typography>)}</Stack>
  </Paper>;
}
